/**
 * useSharedDevices — GET /v1/devices (OPEN-036 / DEC-335): this company's shared tablets.
 *
 * The row type is a structural copy of the API's own, declared here rather than in
 * @lodex-pro/shared (the shared barrel pulls drizzle into a web bundle). Source of truth:
 * packages/api/src/routes/devices/shared-devices.ts (SharedDeviceRow). Change both together.
 */

import { useApiQuery, type useApiOpts } from '@lodex-pro/web-shared';

export interface SharedDeviceRow {
  readonly id: string;
  readonly name: string;
  /** ISO instant. */
  readonly enrolledAt: string;
  /** ISO instant of the tablet's last check-in, or null if it has never checked in. */
  readonly lastSeenAt: string | null;
  /** The driver signed in at that check-in. */
  readonly lastDriverName: string | null;
  /** ISO instant of the latest "Sign out now", or null. */
  readonly clearRequestedAt: string | null;
  /** ISO instant the tablet was last told to sign out, or null. */
  readonly clearHonoredAt: string | null;
  /** A "Sign out now" the tablet has not yet acted on. */
  readonly clearPending: boolean;
}

export interface SharedDevicesResponse {
  readonly devices: readonly SharedDeviceRow[];
}

export const SHARED_DEVICES_QUERY_KEY = ['shared-devices'] as const;

export function useSharedDevices(
  apiOpts: ReturnType<typeof useApiOpts>,
  enabled: boolean,
): ReturnType<typeof useApiQuery<SharedDevicesResponse>> {
  return useApiQuery<SharedDevicesResponse>(apiOpts, '/v1/devices', {
    queryKey: SHARED_DEVICES_QUERY_KEY,
    // A pending sign-out flips to done when the tablet next checks in (every ~5 min).
    refetchInterval: 60_000,
    enabled,
  });
}
